import React from 'react';
import {ActionIcon} from '@mantine/core';
import {SimpleGrid} from 'src/design/atoms/SimpleGrid';
import {Text} from 'src/design/atoms/Text';
import {Tooltip} from 'src/design/atoms/Tooltip';
import {IconTrash} from '@tabler/icons-react';
import type {
  WorkflowDefinitionActionResponse} from 'src/api/ProactApiClient';
import {
  TaskTypeEnum,
  WorkflowDefinitionActionTypeEnum,
} from 'src/api/ProactApiClient';
import {SelectableCard} from 'src/design/molecules/SelectableCard';
import type {IWorkflowDesignerMetadata} from 'src/app/workflows/types';
import {WorkflowItemCard} from './WorkflowItemCard';
import {actionTypeConfigs, getActionConfig} from './actionConfig';
import {SectionLabel} from './SectionLabel';

export interface ActionEditorProps {
  action: WorkflowDefinitionActionResponse;
  onActionChange: (action: WorkflowDefinitionActionResponse) => void;
  metadata: IWorkflowDesignerMetadata;
}

export interface ActionCardProps {
  action: WorkflowDefinitionActionResponse;
  index: number;
  expanded: boolean;
  onToggleExpanded: () => void;
  onActionChange: (
    action: WorkflowDefinitionActionResponse,
    index: number
  ) => void;
  onDeleteRequest: (index: number) => void;
  metadata: IWorkflowDesignerMetadata;
}

const getDefaultMetadata = (
  type: WorkflowDefinitionActionTypeEnum
): WorkflowDefinitionActionResponse['metadata'] => {
  switch (type) {
    case WorkflowDefinitionActionTypeEnum.Email:
      return {emailMetadata: {}, assignTaskMetadata: {}, createTaskMetadata: {}};
    case WorkflowDefinitionActionTypeEnum.CreateTask:
      return {
        emailMetadata: {},
        assignTaskMetadata: {},
        createTaskMetadata: {taskType: TaskTypeEnum.Actions},
      };
    case WorkflowDefinitionActionTypeEnum.ExtendBadgeExpiration:
      return {
        emailMetadata: {},
        assignTaskMetadata: {},
        createTaskMetadata: {},
        extendBadgeExpirationMetadata: {days: 1},
      };
    default:
      return {emailMetadata: {}, assignTaskMetadata: {}, createTaskMetadata: {}};
  }
};

const getActionSummary = (
  action: WorkflowDefinitionActionResponse
): string => {
  const config = getActionConfig(
    action.type ?? WorkflowDefinitionActionTypeEnum.Invalid
  );
  return config?.label ?? 'Action';
};

export const ActionCard: React.FC<ActionCardProps> = ({
  action,
  index,
  expanded,
  onToggleExpanded,
  onActionChange,
  onDeleteRequest,
  metadata,
}) => {
  const handleTypeChange = (value: string) => {
    const newType = Number(value) as WorkflowDefinitionActionTypeEnum;
    if (newType === action.type) return;

    onActionChange(
      {
        ...action,
        type: newType,
        metadata: getDefaultMetadata(newType),
      } as WorkflowDefinitionActionResponse,
      index
    );
  };

  const handleDetailChange = (
    updated: WorkflowDefinitionActionResponse
  ) => {
    onActionChange(updated, index);
  };

  const config = getActionConfig(action.type ?? WorkflowDefinitionActionTypeEnum.Invalid);
  const EditorComponent = config?.editor;

  return (
    <WorkflowItemCard
      index={index}
      expanded={expanded}
      onToggleExpanded={onToggleExpanded}
      summary={getActionSummary(action)}
      title={
        <Text fw={600} size="sm">
          {config?.label ?? 'Action'}
        </Text>
      }
      actions={
        <Tooltip label="Delete action">
          <ActionIcon
            variant="subtle"
            color="red"
            size="md"
            onClick={() => onDeleteRequest(index)}
            aria-label="Delete action">
            <IconTrash size={16} />
          </ActionIcon>
        </Tooltip>
      }>
      <SectionLabel label="Action Type" withSeparation={false} />
      <SimpleGrid cols={{base: 1, sm: 3}}>
        {actionTypeConfigs.map(c => (
          <SelectableCard
            key={c.type}
            label={c.label}
            description={c.description}
            value={String(c.type)}
            selected={action.type === c.type}
            onClick={handleTypeChange}
          />
        ))}
      </SimpleGrid>

      {EditorComponent && (
        <>
          <SectionLabel label="Configuration" />
          <EditorComponent
            action={action}
            onActionChange={handleDetailChange}
            metadata={metadata}
          />
        </>
      )}
    </WorkflowItemCard>
  );
};
